/**
 * Payment API Repository
 *
 * home_karte_api との通信とデータ変換を担当
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';
import type { BillingAddress } from '../types';

// 環境変数からAPIベースURLを取得（未設定時はモック）
const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

// デバッグログ
const sendDebugLog = (message: string): void => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[paymentRepository]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

// 起動時にAPI_BASE_URLをログ出力
sendDebugLog(`API_BASE_URL=${API_BASE_URL}, USE_MOCK=${USE_MOCK}`);

/**
 * 支払い種別
 *
 * API 側 `PAYMENT_TYPE` と一致させる。
 * - cash: 現金
 * - credit: クレジットカード
 * - postpaid: NP 後払い
 * - bill: 請求書払い
 */
export type PaymentMethodCode = 'cash' | 'credit' | 'postpaid' | 'bill';

/**
 * 支払い情報（登録済み）
 */
/**
 * 請求先情報（請求書払い時）
 */
export interface BillingInfo {
  billingTypeId: string;
  name: string;
  zipCode: string;
  addressPrefectureId: string;
  addressCity: string;
  addressDetail: string;
  addressBuilding: string;
}

/**
 * 見積もり内訳グループ
 *
 * API側 `home_karte_api/src/core/config/estimateGroupType.ts` の ESTIMATE_GROUP_TYPE と整合:
 * - 'plan': プラン料金（基本料金）
 * - 'work': 修繕料金（「{name}の修繕合計」として表示）
 * - 'commission': 事務手数料
 */
export interface EstimateGroup {
  name: string;
  total: number;
  type: 'plan' | 'work' | 'commission';
}

/**
 * 支払い情報（登録済み）
 */
export interface PaymentInfo {
  paymentMethodCode: PaymentMethodCode;
  price: number;
  taxRate: number;
  commissionRate: number;
  isPaid: boolean;
  billing: BillingInfo | null;
  /** 見積もり内訳グループ（PPTX指示 0506 スライド12 のお支払い内容確認画面に表示） */
  estimateGroups?: EstimateGroup[];
}

/**
 * 見積もり情報（支払い未登録時）
 * API側で見積もりから金額・税率を返却
 */
export interface EstimateInfo {
  paymentMethodCode: null;
  price: number;
  taxRate: number;
  commissionRate: number;
  isPaid: false;
  billing: null;
  /** 見積もり内訳グループ（同上） */
  estimateGroups?: EstimateGroup[];
}

/**
 * GET /api/v1/inquiries/payment?inquiryId=xxx のレスポンス
 * - PaymentInfo: 支払い登録済み（paymentMethodCodeあり）
 * - EstimateInfo: 支払い未登録（paymentMethodCode: null、見積もりから金額取得）
 * - null: 見積もりも存在しない
 */
export type PaymentOrEstimateInfo = PaymentInfo | EstimateInfo | null;

/**
 * 支払い作成リクエスト
 */
export interface CreatePaymentRequest {
  inquiryId: string;
  paymentMethodCode: PaymentMethodCode;
  price: number;
  taxRate: number;
  creditInfo?: {
    cardNumber: string;
    cardholderName: string;
    expiryMonth: number;
    expiryYear: number;
    securityCode?: string;
  };
  billingAddress?: BillingAddress;
  /**
   * 後払い決済の追加情報。
   * API側 paymentAll.ts schema の superRefine で
   * `paymentMethodCode === 'postpaid'` のときに必須。
   * 未送信だと VALIDATION_REQUIRED エラーとなり /payment-failed に遷移する。
   */
  postpaid?: {
    email: string;
  };
}

/**
 * 支払い作成レスポンス
 */
export interface CreatePaymentResponse {
  redirect: string;
  method: 'GET' | 'POST';
  params: Record<string, string>;
}

/**
 * 領収書作成リクエスト（課題表381）
 *
 * API 側 `createReceiptParamsSchema` は `addressName: z.string()` 必須。
 * 空文字は送らない（画面側で宛名未入力時はボタンを非活性にする）。
 */
export interface CreateReceiptRequest {
  inquiryId: string;
  addressName: string;
}

/**
 * 領収書作成レスポンス
 *
 * 領収書 PDF の署名付き URL。API 実装（home_karte_api createReceipt）は `{ url }`、
 * API 設計書（api_design/homeKarte/paths/inquiries/receipt.yaml）は `{ pdfUrl }` のため、
 * Repository で `url` に正規化する。
 */
export interface CreateReceiptResponse {
  url: string;
}

/**
 * モックモードで返す領収書 PDF URL。
 * inquiryFilesRepository と同じく REACT_APP_MOCK_PDF_URL 未設定時はモックでも失敗させる
 * （API_BASE_URL 未設定の本番で外部のダミー PDF が「領収書」として開かれる事故を防ぐ）。
 */
const MOCK_RECEIPT_PDF_URL = ENV.MOCK_PDF_URL;

/**
 * PaymentInfo型ガード
 * paymentMethodCodeが文字列の場合は支払い登録済み
 */
export const isPaymentInfo = (data: PaymentOrEstimateInfo): data is PaymentInfo => {
  return data !== null && 'paymentMethodCode' in data && data.paymentMethodCode !== null;
};

/**
 * EstimateInfo型ガード
 * paymentMethodCodeがnullの場合は見積もり情報（支払い未登録）
 */
export const isEstimateInfo = (data: PaymentOrEstimateInfo): data is EstimateInfo => {
  return data !== null && 'paymentMethodCode' in data && data.paymentMethodCode === null;
};

/**
 * Payment Repository
 */
export const paymentRepository = {
  /**
   * 支払い情報を取得
   * @param inquiryId 問い合わせID
   * @param token 認証トークン
   * @returns 支払い情報または見積もり情報（未登録の場合はnull）
   */
  getPayment: async (inquiryId: string, token?: string): Promise<ApiResult<PaymentOrEstimateInfo>> => {
    const url = `${API_BASE_URL}/api/v1/inquiries/payment?inquiryId=${inquiryId}`;
    sendDebugLog(`getPayment: url=${url}, hasToken=${!!token}, USE_MOCK=${USE_MOCK}`);

    if (USE_MOCK) {
      // モックモード - 見積もり情報を返す（支払い未登録状態）
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        success: true,
        data: {
          paymentMethodCode: null,
          price: 55000,
          taxRate: 10,
          commissionRate: 3,
          isPaid: false,
          billing: null,
        } as EstimateInfo,
      };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<PaymentInfo | EstimateInfo | Record<string, never>>(
      `/api/v1/inquiries/payment?inquiryId=${inquiryId}`
    );

    if (!result.success) {
      return result;
    }

    // 空オブジェクトの場合はnullを返す
    if (Object.keys(result.data).length === 0) {
      return { success: true, data: null };
    }

    return { success: true, data: result.data as PaymentOrEstimateInfo };
  },

  /**
   * 利用可能な支払方法のコードリストを取得する
   *
   * GET /api/v1/inquiries/payment/method?inquiryId=xxx
   * - 会社の決済ゲートウェイ設定/ネットプロテクションズ設定により、
   *   cash/bill は常に含まれ、credit/postpaid は設定があるときのみ含まれる
   *
   * @param inquiryId 問い合わせID
   * @param token 認証トークン
   */
  getAvailablePaymentMethods: async (
    inquiryId: string,
    token?: string,
  ): Promise<ApiResult<{ paymentMethodCodes: PaymentMethodCode[] }>> => {
    sendDebugLog(`getAvailablePaymentMethods: inquiryId=${inquiryId}, hasToken=${!!token}, USE_MOCK=${USE_MOCK}`);

    if (USE_MOCK) {
      // モックモード - 全方法を利用可能として返す
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        success: true,
        data: { paymentMethodCodes: ['cash', 'bill', 'credit', 'postpaid'] },
      };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    return client.get<{ paymentMethodCodes: PaymentMethodCode[] }>(
      `/api/v1/inquiries/payment/method?inquiryId=${inquiryId}`,
    );
  },

  /**
   * 支払方法のみを記録する（実決済は行わない）
   *
   * POST /api/v1/inquiries/payment/set
   * - 注文サイン前の支払方法事前選択、または支払方法変更時に呼ばれる
   * - DB の `payments.payment_type` を upsert する
   * - 既に `is_paid=true` の場合は API 側で PAID エラーを返す
   *
   * @param inquiryId 問い合わせID
   * @param paymentMethodCode 支払方法
   * @param token 認証トークン
   */
  setPaymentMethod: async (
    inquiryId: string,
    paymentMethodCode: PaymentMethodCode,
    token?: string,
  ): Promise<ApiResult<{ inquiryId: string }>> => {
    // POST /inquiries/payment/set は支払方法の事前選択のみ。
    // billingAddress は決済確定（createPayment）で送るため、API スキーマからも削除済み。
    sendDebugLog(
      `setPaymentMethod: inquiryId=${inquiryId}, method=${paymentMethodCode}, USE_MOCK=${USE_MOCK}`,
    );

    if (USE_MOCK) {
      // モックモード - 成功を返す
      await new Promise(resolve => setTimeout(resolve, 200));
      return { success: true, data: { inquiryId } };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    return client.post<{ inquiryId: string }>('/api/v1/inquiries/payment/set', {
      inquiryId,
      paymentMethodCode,
    });
  },

  /**
   * 支払いを作成/更新
   * @param request 支払い作成リクエスト
   * @param token 認証トークン
   * @returns リダイレクト情報
   */
  createPayment: async (request: CreatePaymentRequest, token?: string): Promise<ApiResult<CreatePaymentResponse>> => {
    if (USE_MOCK) {
      // モックモード - 成功を返す
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        success: true,
        data: {
          redirect: `/inquiries/${request.inquiryId}/payment-complete`,
          method: 'GET',
          params: {},
        },
      };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    return client.post<CreatePaymentResponse>('/api/v1/inquiries/payment', request);
  },

  /**
   * 領収書を発行する（課題表381）
   *
   * POST /api/v1/inquiries/receipt
   * - 宛名付きの領収書 PDF を生成し、署名付き URL を返す
   * - 同一 inquiry で再実行すると API 側で同じファイルを上書きする
   *
   * @param request 問い合わせ ID と宛名
   * @param token 認証トークン
   * @returns 領収書 PDF の URL
   */
  createReceipt: async (
    request: CreateReceiptRequest,
    token?: string,
  ): Promise<ApiResult<CreateReceiptResponse>> => {
    if (USE_MOCK) {
      // モックモード - PDF 生成の待ち時間を模して少し遅らせる
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!MOCK_RECEIPT_PDF_URL) {
        sendDebugLog('createReceipt: モックモードだが REACT_APP_MOCK_PDF_URL が未設定');
        return {
          success: false,
          error: { type: 'unknown', message: '領収書の URL を取得できませんでした' },
        };
      }
      return { success: true, data: { url: MOCK_RECEIPT_PDF_URL } };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.post<{ url?: string; pdfUrl?: string }>(
      '/api/v1/inquiries/receipt',
      request,
    );

    if (!result.success) {
      return result;
    }

    const url = result.data.url ?? result.data.pdfUrl;
    if (!url) {
      sendDebugLog('createReceipt: レスポンスに url / pdfUrl がありません');
      return {
        success: false,
        error: { type: 'unknown', message: '領収書の URL を取得できませんでした' },
      };
    }

    return { success: true, data: { url } };
  },
};
